import { Box } from "@/components/ui/box";
import { Pill, type Tone } from "@/components/ui/pill";
import { EmptyState, QueryState } from "@/components/ui/states";
import { formatDateTime, formatMoney } from "@/lib/format";
import { useScoringRuns } from "../hooks";
import type { ScoringRun } from "../types";

const KIND = { initial: "First scoring", rescore: "Re-score", retry: "Retry" } as const;
const SCOPE = { all: "All items", failed: "Failed scores", selected: "Selected items" } as const;
const TONE: Record<ScoringRun["status"], Tone> = {
  queued: "neutral",
  running: "run",
  completed: "neutral",
  failed: "err",
};
const STATUS = { queued: "Queued", running: "Running", completed: "Completed", failed: "Failed" } as const;

function countsLine(c: ScoringRun["counts"]): string {
  const parts = [`${c.scored} of ${c.total} scored`];
  if (c.errors) parts.push(`${c.errors} errors`);
  if (c.not_applicable) parts.push(`${c.not_applicable} not applicable`);
  return parts.join(" · ");
}

export function ScoringHistory({ experimentId }: { experimentId: string }) {
  const runs = useScoringRuns(experimentId);
  return (
    <Box>
      <QueryState query={runs} what="scoring history">
        {runs.data?.length === 0 ? (
          <EmptyState title="Not scored yet">
            Each scoring of this experiment is listed here, with the evaluator versions it used.
          </EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl text-xs">
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Evaluators</th>
                  <th>Items</th>
                  <th>Result</th>
                  <th className="num">Cost</th>
                  <th>By</th>
                  <th>When</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {runs.data?.map((r) => (
                  <tr key={r.id}>
                    <td className="font-medium">{KIND[r.kind]}</td>
                    <td>
                      <span className="flex flex-wrap gap-1">
                        {r.evaluators.map((ev) => (
                          <span key={`${ev.key}-${ev.version}`}>
                            {ev.name} <span className="font-mono text-faint">v{ev.version}</span>
                          </span>
                        ))}
                      </span>
                    </td>
                    <td>{SCOPE[r.scope]}</td>
                    <td>{countsLine(r.counts)}</td>
                    <td className="num">{r.cost_usd === null ? "Unknown" : formatMoney(r.cost_usd)}</td>
                    <td className="text-muted">{r.created_by}</td>
                    <td className="text-muted">{formatDateTime(r.created_at)}</td>
                    <td>
                      <Pill tone={TONE[r.status]} dot={r.status === "running"}>
                        {STATUS[r.status]}
                      </Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
    </Box>
  );
}
